import * as THREE from 'three'
import { useEffect, useState } from 'react'
import { Canvas, useLoader } from '@react-three/fiber'
import { OrbitControls } from '@react-three/drei'
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader'
import { Results as MediapipeHandsResults } from '@mediapipe/hands'
import { Pose, Landmark, mirrorLandmark } from '../utils/pose'
import { hipQuat, calcBoneRotations, lerpBoneWorldRotation, lerpBoneReset } from '../utils/BlazeBones'
import { useSettings } from '../context/SettingsContext'

interface BoneData {
  bone: THREE.Bone;
  matrix: THREE.Matrix4;
}

const boneMap: Record<number, string> = {
  11: 'mixamorigLeftArm',
  12: 'mixamorigRightArm',
  13: 'mixamorigLeftForeArm',
  14: 'mixamorigRightForeArm',
  15: 'mixamorigLeftHand',
  16: 'mixamorigRightHand',
  23: 'mixamorigLeftUpLeg',
  24: 'mixamorigRightUpLeg',
  25: 'mixamorigLeftLeg',
  26: 'mixamorigRightLeg',
  29: 'mixamorigLeftFoot',
  30: 'mixamorigRightFoot',

  // 'leftHandPinky1': 'mixamorigLeftHandPinky1',
  // 'leftHandPinky2': 'mixamorigLeftHandPinky2',
  // 'leftHandPinky3': 'mixamorigLeftHandPinky3',

  // 'leftHandRing1': 'mixamorigLeftHandRing1',
  // 'leftHandRing2': 'mixamorigLeftHandRing2',
  // 'leftHandRing3': 'mixamorigLeftHandRing3',

  // 'leftHandMiddle1': 'mixamorigLeftHandMiddle1',
  // 'leftHandMiddle2': 'mixamorigLeftHandMiddle2',
  // 'leftHandMiddle3': 'mixamorigLeftHandMiddle3',

  // 'leftHandIndex1': 'mixamorigLeftHandIndex1',
  // 'leftHandIndex2': 'mixamorigLeftHandIndex2',
  // 'leftHandIndex3': 'mixamorigLeftHandIndex3',

  // 'leftHandThumb1': 'mixamorigLeftHandThumb1',
  // 'leftHandThumb2': 'mixamorigLeftHandThumb2',
  // 'leftHandThumb3': 'mixamorigLeftHandThumb3',

  // 'rightHandPinky1': 'mixamorigRightHandPinky1',
  // 'rightHandPinky2': 'mixamorigRightHandPinky2',
  // 'rightHandPinky3': 'mixamorigRightHandPinky3',

  // 'rightHandRing1': 'mixamorigRightHandRing1',
  // 'rightHandRing2': 'mixamorigRightHandRing2',
  // 'rightHandRing3': 'mixamorigRightHandRing3',

  // 'rightHandMiddle1': 'mixamorigRightHandMiddle1',
  // 'rightHandMiddle2': 'mixamorigRightHandMiddle2',
  // 'rightHandMiddle3': 'mixamorigRightHandMiddle3',

  // 'rightHandIndex1': 'mixamorigRightHandIndex1',
  // 'rightHandIndex2': 'mixamorigRightHandIndex2',
  // 'rightHandIndex3': 'mixamorigRightHandIndex3',

  // 'rightHandThumb1': 'mixamorigRightHandThumb1',
  // 'rightHandThumb2': 'mixamorigRightHandThumb2',
  // 'rightHandThumb3': 'mixamorigRightHandThumb3',
}

interface PoseCanvas3DProps {
  // width: number
  // height: number
  poseData: Pose | null
  handsResults: MediapipeHandsResults | null
}

export const PoseCanvas3D = ({ poseData, handsResults }: PoseCanvas3DProps) => {
  // const { width, height } = useWindowSize()
  // const aspect = width / height
  // const frustumSize = 2.67

  return (
    <Canvas
      camera={{ position: [0, 0, 2] }}
      style={{
        width: '100%',
        height: '100%',
        // transform: 'scaleX(-1)',
      }}>
      {/* NOTE: OrthographicCamera has issues with resizing so disabling for now */}
      {/* <OrthographicCamera
        makeDefault
        zoom={1}
        position={[0, 0, 10]}
        left={-frustumSize * aspect / 2}
        right={frustumSize * aspect / 2}
        top={frustumSize / 2}
        bottom={-frustumSize / 2}
        near={0.1}
        far={1000}
      /> */}
      <OrbitControls enableZoom={true} enablePan={true} enableRotate={true} />
      <ambientLight intensity={5} color={0xffffff} />
      <pointLight position={[2, 3, 2]} intensity={55} />
      <FbxModel url="/mixamo.fbx" poseData={poseData} handsResults={handsResults} />
    </Canvas>
  )
}

interface FbxModelProps {
  url: string
  poseData: Pose | null
  handsResults: MediapipeHandsResults | null
}

const FbxModel = ({ url, poseData, handsResults }: FbxModelProps) => {
  const fbx = useLoader(FBXLoader, url)
  const [bones, setBones] = useState<Record<string, BoneData>>({})

  const { settings } = useSettings()

  useEffect(() => {
    const bones: Record<string, BoneData> = {}
    fbx.traverse((child) => {
      if (child.type !== 'Bone' || bones[child.name])
        return

      bones[child.name] = {
        bone: child as THREE.Bone,
        matrix: (child as THREE.Bone).matrix.clone(),
      }
    })

    setBones(bones)
  }, [fbx])

  useEffect(() => {
    if (settings.enableEnhancedHands)
      return

    // RESET FINGERS
    const fingers = ['Thumb', 'Index', 'Middle', 'Ring', 'Pinky']

    for (let handIdx = 0; handIdx < 2; handIdx++) {
      const side = handIdx == 0 ? 'Left' : 'Right'
      for (let fingerIdx = 0; fingerIdx < 5; fingerIdx++) {
        const fingerName = fingers[fingerIdx]

        for (let i = 1; i <= 3; i++) {
          const bone1 = bones[`mixamorig${side}Hand${fingerName}${i}`]?.bone
          if (bone1)
            bone1.rotation.set(0, 0, 0)
        }
      }
    }
  }, [bones, settings.enableEnhancedHands])

  useEffect(() => {
    if (!poseData || !bones)
      return

    const pose = poseData.data//.data.map(([x, y, z]) => [x, y, z])

    const LERP_AMOUNT = 0.25
    // TODO: SMOOTH HANDS

    // TODO: if !settings.enableEnhancedHands reset fingers... (but only on change, not every frame)
    if (handsResults?.multiHandLandmarks && settings.enableEnhancedHands) {
      // TODO:
      // 1. cleanup fingers logic
      // 2. potentially support proximal, medial and distal bones separately
      // 3. unify logic with BlazeBones.ts (where the hand rotation logic is at)
      const fingers = ['Thumb', 'Index', 'Middle', 'Ring', 'Pinky']

      for (let handIdx = 0; handIdx < handsResults.multiHandLandmarks.length; handIdx++) {
        const hand = handsResults.multiHandLandmarks[handIdx]
        const handedness = handsResults.multiHandedness[handIdx]
        const side = handedness.label
        // simple angle calc - setting proximial, medial and distal bones to same angle
        for (let fingerIdx = 0; fingerIdx < 5; fingerIdx++) {
          const fingerName = fingers[fingerIdx]
          const j = fingerIdx * 4 + 1
          // let j = f * 4 + 2

          if (!hand[j] || !hand[j + 1] || !hand[j + 2]) {
            continue
          }

          const indexA = new THREE.Vector3(
            hand[j].x,
            hand[j].y,
            hand[j].z,
          )

          const fingerB = new THREE.Vector3(
            hand[j + 1].x,
            hand[j + 1].y,
            hand[j + 1].z,
          )

          const indexC = new THREE.Vector3(
            hand[j + 2].x,
            hand[j + 2].y,
            hand[j + 2].z,
          )

          const fingerAB = new THREE.Vector3().subVectors(indexA, fingerB)
          const fingerBC = new THREE.Vector3().subVectors(fingerB, indexC)
          let angle = Math.pow(fingerAB.angleTo(fingerBC), 1.2)

          const axis = fingerIdx == 0 ? 'z' : 'x'
          if (fingerIdx == 0) {
            // angle -= Math.PI/8
            // angle *= 2
            if (side == 'Left') {
              angle = -angle
            }
          }

          for (let i = 1; i <= 3; i++) {
            const bone = bones[`mixamorig${side}Hand${fingerName}${i}`]?.bone
            if (bone)
              bone.rotation[axis] = THREE.MathUtils.lerp(bone.rotation[axis], angle, LERP_AMOUNT)
          }
        }
      }
    }

    // hips rotation: calculated differently
    const hipsBone = bones['mixamorigHips']?.bone
    if (hipsBone) {
      const quatHip = hipQuat(pose)
      hipsBone.quaternion.slerp(quatHip, LERP_AMOUNT)

      // hips position: using 2d pose data
      const leftHip = poseData.pos2d(Landmark.LEFT_HIP)
      const rightHip = poseData.pos2d(Landmark.RIGHT_HIP)
      const centerHips = new THREE.Vector3(
        (leftHip.x + rightHip.x) / 2,
        (leftHip.y + rightHip.y) / 2,
        0,
      )

      hipsBone.position.lerp(
        new THREE.Vector3(
          // pepe:
          // -2000 * (centerHips.x - 0.5) - 150,
          // -2000 * (centerHips.y - 0.5) + 1000,
          // mixamo:
          -150 * centerHips.x + 75,
          -200 * centerHips.y + 200,
          0,
        ),
        LERP_AMOUNT,
      )
    }

    const rotations = calcBoneRotations(pose, handsResults as any as MediapipeHandsResults)
    for (const [key, quat] of Object.entries(rotations)) {
      const boneName = boneMap[key as any]
      if (!boneName) {
        continue
      }

      const bone = bones[boneName]
      if (!bone) {
        continue
      }

      if (quat) {
        // TODO: unify thresholds
        const mirrorKey = mirrorLandmark(parseInt(key))
        if (poseData.conf(mirrorKey) < 0.9) { // || poseData.conf((key as any as number) - 2) < 0.7) {
          lerpBoneReset(bone.bone, LERP_AMOUNT / 4)
          continue
        }

        lerpBoneWorldRotation(bone.bone, quat, LERP_AMOUNT)
      }
    }
  }, [poseData, bones, settings.enableEnhancedHands, handsResults])

  return <primitive
    object={fbx}
    scale={0.0105}
    position={[0, -1, 0]}
  />
}
